import React, { useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Checkbox } from 'components/UIElements'
import { formatInnerHTML } from 'utils/misc'

const CHOICES_WIDTH = {
  2: 30,
  3: 50,
  4: 60,
  5: 70,
  6: 70,
  7: 80,
}

const MatrixRow = props => {
  const { id, isHeader, isLastRow, isSibling, question, questionId, setAllRequiredAnswered, siblingOrder, updateMatrixRowQuestion } =
    props

  const [selected, setSelected] = useState([])
  const [selectingChoice, setSelectingChoice] = useState(null)
  const [deselectingChoice, setDeselectingChoice] = useState(null)

  const deselectTimeoutRef = useRef(null)
  const selectTimeoutRef = useRef(null)
  const choicesElementRef = useRef(null)

  useEffect(() => {
    return () => {
      if (deselectTimeoutRef.current) clearTimeout(deselectTimeoutRef.current)
      if (selectTimeoutRef.current) clearTimeout(selectTimeoutRef.current)
    }
  }, [])

  const { choices_order, questions, attributes, choices } = question
  const isSelectMultiple = !!attributes?.allowMultipleAnswers

  const labelStyle = useMemo(() => {
    return {
      width: `${100 - CHOICES_WIDTH[choices_order.length]}%`,
    }
  }, [choices_order.length])

  const completeMatrixRowQuestion = nextSelected => {
    if (nextSelected.length) {
      const answer = nextSelected.map(choiceId => {
        return {
          label: choices[choiceId].label,
          value: choiceId,
          score_value: choices[choiceId].score_value,
        }
      })
      updateMatrixRowQuestion(questionId, answer)
    } else {
      updateMatrixRowQuestion(questionId, [])
    }
  }

  const onSelectChoice = choiceId => {
    const alreadySelected = selected.includes(choiceId)
    let nextSelected = selected.slice(0)

    if (alreadySelected) {
      nextSelected = isSelectMultiple ? nextSelected.filter(el => el !== choiceId) : [choiceId]
      setSelected(nextSelected)
      setDeselectingChoice(choiceId)
      completeMatrixRowQuestion(nextSelected)
      if (deselectTimeoutRef.current) clearTimeout(deselectTimeoutRef.current)
      deselectTimeoutRef.current = setTimeout(() => {
        setDeselectingChoice(null)
      }, 200)
    } else {
      nextSelected = isSelectMultiple ? [...nextSelected, choiceId] : [choiceId]
      setSelected(nextSelected)
      setSelectingChoice(choiceId)
      completeMatrixRowQuestion(nextSelected)
      if (selectTimeoutRef.current) clearTimeout(selectTimeoutRef.current)
      selectTimeoutRef.current = setTimeout(() => {
        setSelectingChoice(null)
      }, 300)
    }

    const lastSiblingId = isSibling ? siblingOrder.slice(-1)[0] : id
    setAllRequiredAnswered(false, lastSiblingId, [], siblingOrder)
  }

  const renderHeader = () => {
    const headerStyle = {
      width: `${CHOICES_WIDTH[choices_order.length]}%`,
    }

    return (
      <div className='header-labels flexed space-between-justified' style={headerStyle}>
        {choices_order.map(choiceId => {
          return <span key={choiceId} dangerouslySetInnerHTML={{ __html: formatInnerHTML(choices[choiceId].label) }} />
        })}
      </div>
    )
  }

  const renderChoices = () => {
    const questionChoicesStyle = {
      width: `${CHOICES_WIDTH[choices_order.length]}%`,
    }

    return (
      <div className='question-choices flexed space-between-justified' style={questionChoicesStyle} ref={choicesElementRef}>
        {choices_order.map(choiceId => {
          return (
            <Button
              onClick={() => onSelectChoice(choiceId)}
              key={`${choiceId}`}
              className={`matrix choice${selectingChoice === choiceId ? ' selecting' : ''}${
                selected.includes(choiceId) ? ' selected' : ''
              }${deselectingChoice === choiceId ? ' deselecting' : ''}${isSelectMultiple ? ' multiple' : ''}`}>
              <Checkbox
                check={isSelectMultiple}
                checked={selected.includes(choiceId)}
                transition={choiceId === selectingChoice || choiceId === deselectingChoice}
              />
            </Button>
          )
        })}
      </div>
    )
  }

  return (
    <div className={`matrix-row custom-width flexed ${isHeader ? 'row-header' : ''} ${isLastRow ? 'last-row' : ''}`}>
      {isHeader ? (
        renderHeader()
      ) : (
        <>
          <div className='row-label' style={labelStyle}>
            <div className='sub-question-label' dangerouslySetInnerHTML={{ __html: formatInnerHTML(questions[questionId].label) }} />
          </div>
          {renderChoices()}
        </>
      )}
    </div>
  )
}

export default MatrixRow

MatrixRow.propTypes = {
  id: PropTypes.string,
  isHeader: PropTypes.bool,
  isLastRow: PropTypes.bool,
  isSibling: PropTypes.bool,
  questionId: PropTypes.string,
  question: PropTypes.object,
  setAllRequiredAnswered: PropTypes.func,
  siblingOrder: PropTypes.arrayOf(PropTypes.string),
  updateMatrixRowQuestion: PropTypes.func,
}
